import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { AssessmentCentralService } from "../../../shared/services/assessment-central.service";
import { MessageService } from "../../../shared/services/message.service";
import { TagsService } from "../../../shared/services/tag.service";
import { DeletePopupComponent } from "../../../shared/components/delete-popup/delete-popup.component";
import { FilterService } from "../../../shared/services/filter.service";
import { TemplateCreateModalDialogComponent } from "../../../shared/components/template-create-modal/template-create-modal";
import { IncidentTrackerAssessmentCreateModalDialogComponent } from "./incident-tracker-assessment-create-modal/incident-tracker-assessment-create-modal";
import { IncidentTrackerService } from "../../../shared/services/incident-tracker.service";
import { hasValue } from "../../../shared/helpers/filter-utils";
import { forkJoin, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { LoggingService } from "../../../logging.service";
import { AuthService } from "../../../shared/services/auth.service";
import { BreadcrumbService } from "../../../shared/services/breadcrumb.service";
import { CommonService } from "../../../shared/services/common.service";
import textConfiguration from "../../../../assets/static-text-configuration.json";
import { AuditTemplateEditDialogComponent } from "../../../shared/components/audit-template-edit-dialog/audit-template-edit-dialog.component";

@Component({
  selector: "assessment-preview-modal",
  templateUrl: "assessment-preview-modal.html",
  styleUrls: ["assessment-preview-modal.scss"],
})
export class AssessmentPreviewModalDialogComponent implements AfterViewInit {
  template: any;
  questions: any = [];
  name = "";
  category = "";
  description: any;
  applicability: any;
  categoryObject: any;
  categoriesList: any;
  tagsList: any;
  showTags = false;
  moduleName: string = (textConfiguration as any)?.modules?.audits;
  @ViewChild("requiredDocsPopUp") requiredDocsPopUp: TemplateRef<any>;
  @ViewChild("checklistPopup") checklistPopup: TemplateRef<any>;

  selectedRequiredDocs: string[] = [];
  requiredDocsDialogRef: MatDialogRef<any>;
  selectedChecklistItems = [];
  checklistDialogRef: any;
  userDetails: any;
  auditOwner: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AssessmentPreviewModalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly incidentTrackerService: IncidentTrackerService,
    private readonly spinner: NgxSpinnerService,
    private readonly authService: AuthService,
    private readonly dialog: MatDialog
  ) {}

  ngAfterViewInit() {
    this.authService.tagsEnabled.subscribe((res: any) => {
      this.showTags = res;
    });
    this.template = this.data.dataKey;
    this.name = this.template?.title;
    this.tagsList = this.data.tagsList?.map((item: any) => {
      if (item?.id) {
        return item.id;
      } else {
        return item;
      }
    });
    this.description = this.template.description;
    this.applicability = this.template.applicability;
    this.category = this.template.type.display_name;
    this.template.questions.forEach((element: any) => {
      const domainIndex = this.questions.findIndex(
        (e: any) => e.domain === element.domain
      );
      if (domainIndex > -1) {
        const subdomainIndex = this.questions[domainIndex].subdomains.findIndex(
          (s: any) => s.sub_domain === element.sub_domain
        );
        if (subdomainIndex > -1) {
          this.questions[domainIndex].subdomains[subdomainIndex].questions.push(
            element
          );
        } else {
          this.questions[domainIndex].subdomains.push({
            sub_domain: element.sub_domain,
            questions: [element],
          });
        }
      } else {
        this.questions.push({
          domain: element.domain,
          subdomains: [
            {
              sub_domain: element.sub_domain,
              questions: [element],
            },
          ],
        });
      }
    });
    this.handlePermission();
  }

  handlePermission() {
    this.userDetails = JSON.parse(localStorage.getItem("permission") ?? "{}");
    const isModuleOwner = this.userDetails.is_module_owner;
    const moduleOwner = this.userDetails.owned_modules.includes(
      this.moduleName
    );
    this.auditOwner =
      (isModuleOwner && moduleOwner) ||
      (this.userDetails.accessible_modules || []).includes(this.moduleName) ||
      this.userDetails.is_superuser;
  }

  download() {
    this.incidentTrackerService
      .downloadResponseTemplate(this.data.dataKey.id)
      .subscribe(
        (res: any) => {
          const downloadLink = document.createElement("a");
          downloadLink.href = window.URL.createObjectURL(res);
          downloadLink.setAttribute("download", this.name);
          document.body.appendChild(downloadLink);
          downloadLink.click();
        },
        (error: any) => {
          this.spinner.hide();
          throw error;
        }
      );
  }

  getDocumentCount(question: any): number {
    return (
      question.checklist_items?.reduce(
        (count: any, item: any) =>
          count + (item.required_documents?.length || 0),
        0
      ) || 0
    );
  }

  openChecklistDialog(question: any): void {
    this.selectedChecklistItems = question.checklist_items || [];

    this.checklistDialogRef = this.dialog.open(this.checklistPopup, {
      width: "750px",
      disableClose: false,
    });
  }

  closeModal(bool = false): void {
    this.dialogRef.close(bool);
  }

  closeDialog() {
    this.dialogRef.close();
  }
}

@Component({
  selector: "app-incident-tracker-templates",
  templateUrl: "./incident-tracker-templates.component.html",
  styleUrls: ["./incident-tracker-templates.component.scss"],
})
export class IncidentTrackerTemplatesComponent implements OnInit, OnDestroy {
  moduleName: string = (textConfiguration as any)?.modules
    ?.assessment_and_survey;
  templates: any = [];
  filteredTemplate: any = [];
  unCategorizedTemplates: any = [];
  isCategoryView = false;
  file: any;
  readMode: any = false;
  filterObj: any;
  dataLoaded = false;
  filterCfg: any;
  isRightPaneCollapsed = true;

  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly assessmentCentralService: AssessmentCentralService,
    private readonly spinner: NgxSpinnerService,
    private readonly messageService: MessageService,
    private readonly toastrService: ToastrService,
    private readonly tagService: TagsService,
    private readonly route: ActivatedRoute,
    private readonly filterService: FilterService,
    private readonly incidentTrackerService: IncidentTrackerService,
    private readonly loggingService: LoggingService,
    private readonly breadCrumbService: BreadcrumbService,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.filterService.setModuleContext(this.moduleName);
    this.initFilterCfg();
    this.messageService.setGnews({
      pagename: "Assessment Central - Templates",
    });
    this.route.queryParamMap.subscribe((params) => {
      this.readMode = params.get("readMode") === "1";
    });
    this.filterService.filterValues
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: any) => {
        this.filterObj = res;
        let awl = false;
        this.filterObj?.inputs?.forEach((element: any) => {
          if (hasValue(element.value, this.loggingService)) {
            awl = true;
          }
        });
        if (awl) {
          this.search();
        } else {
          this.getTemplates();
        }
      });
    const breadcrumb = [
      { label: "Audits", url: "/incidentracker/audits" },
      { label: "Library" },
    ];
    this.breadCrumbService.setBreadcrumb(breadcrumb);
  }

  initFilterCfg() {
    const searchableSelect = "searchable-select";
    forkJoin({
      types: this.filterService.getTypes(),
      templateNames: this.getTemplateNames(),
      owners: this.getOwnersList(),
      tags: this.getTag(),
    }).subscribe(({ types, templateNames, owners, tags }) => {
      this.filterCfg = {
        inputs: [
          {
            field: "type",
            type: searchableSelect,
            placeholder: "Search Type...",
            options: types,
            value: null,
          },
          {
            field: "name",
            type: searchableSelect,
            placeholder: "Search Name...",
            options: templateNames,
            value: null,
          },
          {
            field: "applicability",
            type: "input",
            placeholder: "Search Applicability...",
            value: null,
          },
          {
            field: "tag",
            type: "searchable-multiselect",
            placeholder: "Search Tag...",
            options: tags,
            value: [],
          },
          {
            field: "owner",
            type: searchableSelect,
            placeholder: "Search Owner...",
            options: owners,
            value: null,
          },
        ],
      };
      this.filterService.filterCfgs.next(this.filterCfg);
    });
  }

  getTemplateNames() {
    return this.filterService.getNames().pipe(
      map((res: any) => {
        return res.map((name: any) => ({
          ...name,
          id: name.title,
          display_name: name.title,
        }));
      })
    );
  }

  getTag() {
    return this.filterService.getTag().pipe(
      map((res: any) => {
        return res.map((tag: any) => ({
          ...tag,
          name: tag.tag_name,
        }));
      })
    );
  }

  getOwnersList() {
    return this.assessmentCentralService.getOwnersList().pipe(
      map((res: any) => {
        return res.map((owner: any) => ({
          ...owner,
          display_name: `${owner.first_name} ${owner.last_name}`,
        }));
      })
    );
  }

  getTemplates() {
    this.dataLoaded = false;
    this.incidentTrackerService.getAuditTemplates().subscribe((res: any) => {
      res.forEach((category: any) => {
        category.templates.forEach((element: any) => {
          element.color = this.getGMapRandomColor();
        });
      });
      this.templates = res;
      this.filteredTemplate = res;
      this.unCategorizedTemplates = (
        this.filteredTemplate?.reduce(
          (acc: any, category: any) => [...acc, ...category.templates],
          []
        ) || []
      ).sort(
        (a: any, b: any) =>
          new Date(b.last_updated_on).getTime() -
          new Date(a.last_updated_on).getTime()
      );
      this.dataLoaded = true;
    });
  }

  templateDownload() {
    this.incidentTrackerService.downloadTemplate().subscribe(
      (res: any) => {
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(res);
        const fileName = "sample_template";
        downloadLink.setAttribute("download", fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
      },
      (error: any) => {
        this.spinner.hide();
        throw error;
      }
    );
  }

  previewAssessment(template: any, tags: any) {
    const dialog = this.dialog.open(AssessmentPreviewModalDialogComponent, {
      width: "50vw",
      disableClose: true,
      autoFocus: false,

      data: {
        dataKey: template,
        tagsList: tags,
      },
    });
    dialog.afterClosed().subscribe((data) => {
      if (data) {
        this.proceed(template, tags);
      }
    });
  }

  proceed(template: any, tags: any) {
    let dialog = this.dialog.open(
      IncidentTrackerAssessmentCreateModalDialogComponent,
      {
        width: "500px",
        disableClose: true,
        autoFocus: false,

        data: {
          template_id: template.id,
          template_name: template.title,
          moduleName: this.moduleName,
        },
      }
    );

    dialog.afterClosed().subscribe((status: any) => {
      if (status) {
        this.previewAssessment(template, tags);
      }
    });
  }

  async addTags(id: any, selectedTags: any) {
    if (selectedTags?.length > 0) {
      await selectedTags.forEach(async (element: any) => {
        const pst = {
          entity_id: id,
          entity_type: "grc_audit_template",
          tag_id: element.id,
        };
        this.tagService.magTag(pst).subscribe(() => {});
      });
    }
  }

  onFilechange(event: any) {
    const selectedFile = event.target.files[0];
    this.commonService.validateFileSize(selectedFile).subscribe((isValid) => {
      if (!isValid) {
        this.toastrService.error("Please upload less than 50MB");
        return;
      }
      this.file = selectedFile;
      const dialog = this.dialog.open(TemplateCreateModalDialogComponent, {
        width: "500px",
        disableClose: true,
        autoFocus: false,

        data: {
          uploadedFile: this.file,
        },
      });
      dialog.afterClosed().subscribe((data) => {
        if (data) {
          const formData = new FormData();
          formData.append("file", this.file);
          formData.append("file_name", this.file.name);
          formData.append("title", data.title);
          formData.append("description", data.description);
          formData.append("applicability", data.applicability);
          formData.append("focus_area", data.focus_area);
          formData.append(
            "is_external_assessment",
            data.is_external_assessment
          );
          formData.append("internal_assessor", data.internal_assessor);
          formData.append("category", data.category);
          formData.append("frequency", data.frequency);
          formData.append("type", data.type);
          formData.append("external_assessor", data.external_assessor);
          this.incidentTrackerService
            .uploadFile(formData)
            .subscribe((response: any) => {
              this.spinner.hide();
              if (response) {
                this.file = [];
                this.addTags(response.id, data.selectedTags);
                this.previewAssessment(response, data.selectedTags);
                this.getTemplates();
              }
            });
        }
        event.target.value = "";
      });
    });
  }

  getGMapRandomColor() {
    const colorNum = 360;
    const color = Math.floor(Math.random() * colorNum);
    return `hsla(${color}, 50%, 25%, 90%)`;
  }

  getFilterQueryParams() {
    let qprm = "";
    this.filterObj?.inputs?.forEach((element: any) => {
      const value = element.value;
      if (value) {
        if (element.field === "type") {
          qprm += `&type=${value.id}`;
        } else if (element.field === "name") {
          qprm += `&title=${value.id}`;
        } else if (element.field === "applicability") {
          qprm += `&applicability=${value}`;
        } else if (element.field === "owner") {
          qprm += `&created_by=${value.id}`;
        } else if (element.field === "tag") {
          let tags: any = [];
          value.forEach((tag: any) => {
            tags.push(tag.id);
          });
          tags = tags.join("|");
          qprm += `&tags=${tags}`;
        } else {
          this.loggingService.warn("Unexpected value");
        }
      }
    });
    return qprm;
  }

  search() {
    this.dataLoaded = false;
    const qParam = this.getFilterQueryParams();
    this.incidentTrackerService
      .getFilteredAuditTemplates(qParam)
      .subscribe((res: any) => {
        res.forEach((category: any) => {
          category.templates.forEach((element: any) => {
            element.color = this.getGMapRandomColor();
          });
        });
        this.templates = res;
        this.filteredTemplate = res;
        this.unCategorizedTemplates = (
          this.filteredTemplate?.reduce(
            (acc: any, category: any) => [...acc, ...category.templates],
            []
          ) || []
        ).sort(
          (a: any, b: any) =>
            new Date(b.last_updated_on).getTime() -
            new Date(a.last_updated_on).getTime()
        );
        this.dataLoaded = true;
      });
  }

  deleteTemplate(id: any, name: string) {
    const dialogRef = this.dialog.open(DeletePopupComponent, {
      disableClose: true,
      data: name,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.incidentTrackerService.deletedAuditTemplate(id).subscribe(() => {
          this.toastrService.success("Template deleted successfully");
          this.getTemplates();
        });
      }
    });
  }

  blankPlan() {
    this.proceed(null, null);
  }

  navigateToIncidentTracker() {
    this.router.navigateByUrl("incidentracker/audits");
  }

  navigateToLibrary() {
    this.router.navigateByUrl(`incidentracker/templates?readMode=0`);
  }

  toggleView() {
    this.isCategoryView = !this.isCategoryView;
  }

  goBack() {
    this.router.navigate(["incidentracker/audits"]);
  }

  toggleRightPane() {
    this.isRightPaneCollapsed = !this.isRightPaneCollapsed;
  }

  createTemplateManually() {
    const dialogRef = this.dialog.open(AuditTemplateEditDialogComponent, {
      width: "90vw",
      maxWidth: "90vw",
      disableClose: true,
      autoFocus: false,
      data: {
        template: {},
        isCreateMode: true,
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) {
        return;
      }

      this.toastrService.success("Template created successfully");
      this.addTags(result.response.id, result.selectedTags);
      this.previewAssessment(result.response, result.selectedTags);
      this.getTemplates();
    });
  }

  ngOnDestroy() {
    this.filterService.setModuleContext("");
    this.filterService.filterCfgs.next({});
    this.filterService.setFilterValue("");
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
